import { BDDialog } from '@brightdrop/bd-ui';
import { translateText } from '@gm-commercial/translations';
import { Box, Checkbox, useMediaQuery, useTheme } from '@mui/material';
import type { TableContainerProps } from '@mui/material/TableContainer';
import clsx from 'clsx';
import type {
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowData,
  MRT_RowSelectionState,
} from 'material-react-table';
import {
  createContext,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { Table } from '../Table';
import type {
  TableModalContentContextValue,
  TableModalProps,
} from './TableModal.model';
import useStyles from './TableModal.styles';

const TABLE_CONTAINER_HEIGHT_PX = 450;
const BANNER_HEIGHT_PX = 64;

const TableModalContentContext =
  createContext<TableModalContentContextValue | null>(null);

const toggleSelectionForIds = (
  _currentSelection: MRT_RowSelectionState,
  ids: string[],
  checked: boolean,
  setSelection: (updater: SetStateAction<MRT_RowSelectionState>) => void
): void => {
  if (checked) {
    setSelection((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, true])),
    }));
  } else {
    setSelection((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => !ids.includes(id))
      )
    );
  }
};

/**
 * Stable wrapper component passed to BDDialog. Reads from context so it never changes identity;
 * only re-renders when context value updates, avoiding dialog content remount and image flicker.
 */
const TableModalContent = (): JSX.Element => {
  const context = useContext(TableModalContentContext);
  if (!context) {
    return <div />;
  }
  return <TableModalContentView context={context} />;
};

const TableModalContentView = ({
  context,
}: {
  context: TableModalContentContextValue;
}): JSX.Element => {
  const {
    subtitle,
    columns,
    data,
    readMode,
    totalCount,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
    onSelectionChange,
    isLoading,
    handleRowSelectionChange,
    getRowId,
    toolbarConfig,
    hideBanner,
    bannerProps,
    shrinkToFit,
    singleSelect,
  } = context;

  const { classes } = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

  // In single-select mode, once a row is selected every other row's checkbox is
  // disabled until that row is unchecked — including rows on other pages or in
  // search results. The selection is controlled state, so it persists across
  // server-side pagination/search; to choose a different row the user must
  // return to their selection and uncheck it first.
  const enableRowSelection = useMemo(() => {
    if (readMode) return false;
    if (!singleSelect) return true;
    const hasSelection = Object.keys(rowSelection).some(
      (id) => rowSelection[id]
    );
    // MRT passes its own row object here; `row.id` is the already-resolved id
    // (computed via the table's getRowId), so use it directly rather than
    // re-running getRowId on a shape it was not designed for.
    return (row: MRT_Row<MRT_RowData>) =>
      !hasSelection || !!rowSelection[row.id];
  }, [readMode, singleSelect, rowSelection]);

  const activeCount = Object.keys(rowSelection).length;
  const isBannerVisible = activeCount > 0 && !hideBanner;
  const containedScroll = !shrinkToFit;
  const tableHeight = isBannerVisible
    ? TABLE_CONTAINER_HEIGHT_PX - BANNER_HEIGHT_PX
    : TABLE_CONTAINER_HEIGHT_PX;

  const muiTableContainerProps = useMemo((): TableContainerProps => {
    if (containedScroll) {
      return {
        className: 'table-container table-container-scroll',
        sx: {
          overflowY: 'auto',
          overflowX: 'auto',
        },
      };
    }

    return {
      className: 'table-container table-container-scroll',
      sx: {
        height: shrinkToFit ? 'auto' : `${tableHeight}px`,
        overflowY: 'auto',
        overflowX: 'auto',
        maxHeight: shrinkToFit ? `${tableHeight}px` : 'none',
      },
    };
  }, [containedScroll, tableHeight, shrinkToFit]);

  const activeIds = useMemo(
    () => data.map((row) => getRowId(row)),
    [data, getRowId]
  );
  const isAllRowsSelectedPage =
    activeIds.length > 0 && activeIds.every((id) => rowSelection[id]);
  const isIndeterminate = activeIds.some((id) => rowSelection[id]);

  const handleHeaderSelectAll = useCallback(
    (checked: boolean) => {
      toggleSelectionForIds(rowSelection, activeIds, checked, setRowSelection);
      for (const row of data) {
        const id = getRowId(row);
        if (activeIds.includes(id)) onSelectionChange?.(row, checked);
      }
    },
    [
      rowSelection,
      activeIds,
      setRowSelection,
      data,
      getRowId,
      onSelectionChange,
    ]
  );

  return (
    <div className={clsx(containedScroll && classes.containedRoot)}>
      {subtitle && <Box className={classes.description}>{subtitle}</Box>}
      <div
        className={clsx(
          classes.tableWrapper,
          containedScroll && classes.tableWrapperContained
        )}
      >
        <Box
          className={clsx(
            classes.table,
            containedScroll && classes.tableContained
          )}
        >
          <Table
            tablePropsObj={{
              columns,
              data,
              enableStickyHeader: true,
              rowCount: totalCount,
              enableSelectAll: !readMode && !singleSelect && totalCount !== 0,
              initialState: { pagination },
              onPaginationChange: setPagination,
              state: {
                pagination,
                rowSelection,
                isLoading,
              },
              onRowSelectionChange: handleRowSelectionChange,
              enableRowSelection,
              enablePagination: true,
              getRowId: (row: MRT_RowData) => getRowId(row),
              muiTableContainerProps,
              displayColumnDefOptions: {
                'mrt-row-select': {
                  Header: singleSelect ? undefined : (
                    <Checkbox
                      checked={isAllRowsSelectedPage}
                      indeterminate={isIndeterminate}
                      onChange={({ target }) =>
                        handleHeaderSelectAll(target.checked)
                      }
                      data-testid="table-modal-select-all-checkbox"
                      aria-label={translateText('common:muiTable.selectAll')}
                    />
                  ),
                  muiTableHeadCellProps: {
                    className: 'select-all-checkbox-header',
                  },
                  muiTableBodyCellProps: ({ row }) => ({
                    sx: { cursor: row.getCanSelect() ? 'pointer' : 'default' },
                    onClick: (event: React.MouseEvent) => {
                      event.stopPropagation();
                      if (row.getCanSelect()) row.toggleSelected();
                    },
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.stopPropagation();
                        event.preventDefault();
                        if (row.getCanSelect()) row.toggleSelected();
                      }
                    },
                  }),
                },
              },
            }}
            tableToolbarConfig={toolbarConfig}
            renderBanner={hideBanner ? undefined : bannerProps}
            enableMobileViewLayout={isMobileScreen}
            containedScroll={containedScroll}
          />
        </Box>
      </div>
    </div>
  );
};

const TableModalInner = <DataEntity extends MRT_RowData>({
  isOpen,
  onClose,
  title,
  subtitle,
  buttonsConfig,
  toolbarConfig,
  columns,
  dataController,
  onSelectionChange,
  selectAllConfig,
  customActionsContent,
  hideBanner = false,
  drawerMobileView = true,
  readMode = false,
  labels,
  shrinkToFit = false,
  singleSelect = false,
}: TableModalProps<DataEntity>) => {
  const {
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    data,
    totalCount,
    isLoading,
    getRowId,
  } = dataController;

  const dataById = useMemo(
    () => new Map(data.map((row) => [getRowId(row), row])),
    [data, getRowId]
  );

  const activeCount = Object.keys(rowSelection).length;

  const handleRowSelectionChange = useCallback(
    (updater: SetStateAction<MRT_RowSelectionState>) => {
      setRowSelection((prev) => {
        const resolved =
          typeof updater === 'function' ? updater(prev) : updater;
        // In single-select mode, clamp to one row: if a new row was just
        // selected, keep only it; otherwise keep the (de)selection as-is.
        const next = singleSelect
          ? (() => {
              const newlySelected = Object.keys(resolved).filter(
                (id) => resolved[id] && !prev[id]
              );
              return newlySelected.length > 0
                ? { [newlySelected[newlySelected.length - 1]]: true }
                : Object.fromEntries(
                    Object.entries(resolved).filter(([, selected]) => selected)
                  );
            })()
          : resolved;
        const prevSet = new Set(Object.keys(prev));
        const nextSet = new Set(Object.keys(next));
        for (const id of nextSet) {
          const nowSelected = !!next[id];
          const wasSelected = !!prev[id];
          if (nowSelected !== wasSelected) {
            const row = dataById.get(id);
            if (row) onSelectionChange?.(row, nowSelected);
          }
        }
        for (const id of prevSet) {
          if (!nextSet.has(id) && prev[id]) {
            const row = dataById.get(id);
            if (row) onSelectionChange?.(row, false);
          }
        }
        return next;
      });
    },
    [dataById, onSelectionChange, setRowSelection, singleSelect]
  );

  const handleBannerClose = useCallback(() => {
    handleRowSelectionChange({});
  }, [handleRowSelectionChange]);

  const bannerProps = useMemo(
    () => ({
      selectedCounter: activeCount,
      isOpen: activeCount > 0,
      onCloseBanner: handleBannerClose,
      disableViewportSticky: true,
      ...(selectAllConfig && { selectAllConfig }),
    }),
    [activeCount, handleBannerClose, selectAllConfig]
  );

  return (
    <TableModalContentContext.Provider
      value={{
        subtitle,
        columns: columns as MRT_ColumnDef<MRT_RowData, unknown>[],
        data: data as MRT_RowData[],
        totalCount,
        readMode,
        pagination,
        setPagination,
        rowSelection,
        setRowSelection,
        onSelectionChange: onSelectionChange as (
          data: MRT_RowData,
          selected: boolean
        ) => void,
        isLoading,
        handleRowSelectionChange,
        getRowId: getRowId as (row: MRT_RowData) => string,
        toolbarConfig,
        hideBanner,
        bannerProps,
        shrinkToFit,
        singleSelect,
      }}
    >
      <BDDialog
        disableBackdropClick
        openModal={isOpen}
        containedContentScroll={!shrinkToFit}
        title={title}
        component={TableModalContent}
        buttonsConfig={buttonsConfig}
        customActionsContent={customActionsContent}
        drawerMobileView={drawerMobileView}
        labels={labels}
        onCloseModal={onClose}
      />
    </TableModalContentContext.Provider>
  );
};

/**
 * Generic TableModal<DataEntity> wraps BDDialog with title, subtitle, a data table (with configurable columns,
 * toolbar, and optional selection banner), and action buttons. A custom data controller must be
 * passed in to supply table state and data.
 */
function TableModal<DataEntity extends MRT_RowData>(
  props: TableModalProps<DataEntity>
): JSX.Element {
  return <TableModalInner {...props} />;
}

export default TableModal;
